import { useEffect, useMemo, useState } from "react";
import { api, fmtEur, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Split } from "lucide-react";
import { toast } from "sonner";
import { AZIENDA } from "@/components/PadroncinoSelect";

const r2 = (v) => Math.round(v * 100) / 100;
const VERIFICA = "__verifica";

export const QuoteEditor = ({ padroncini, totale, quote, setQuote }) => {
  const [cerca, setCerca] = useState("");
  const t = cerca.trim().toLowerCase();
  const visibili = padroncini.filter((p) => !t || quote[p.id] || `${p.codice} ${p.ragione_sociale || ""} ${p.nome} ${p.cognome || ""}`.toLowerCase().includes(t));
  const somma = useMemo(() => Object.values(quote).reduce((a, v) => a + (parseFloat(v) || 0), 0), [quote]);
  const diff = r2(totale - somma);
  const dividi = () => {
    const sel = Object.keys(quote).filter((k) => quote[k] !== "" && quote[k] != null);
    const ids = sel.length ? sel : padroncini.map((p) => p.id);
    if (!ids.length) return;
    const q = Math.floor((totale / ids.length) * 100) / 100;
    const out = Object.fromEntries(ids.map((id) => [id, q]));
    out[ids[0]] = r2(q + r2(totale - q * ids.length));
    setQuote(out);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span>Totale <span className="font-mono font-bold">{fmtEur(totale)}</span>
          <span className={`ml-3 font-mono ${Math.abs(diff) < 0.01 ? "text-green-600" : "text-destructive"}`} data-testid="split-diff">
            Da ripartire: {fmtEur(diff)}</span></span>
        <Button size="sm" variant="outline" className="h-7 gap-1" onClick={dividi} data-testid="split-equal">
          <Split className="w-3.5 h-3.5" />Parti uguali
        </Button>
      </div>
      <div className="text-xs text-muted-foreground">Compila solo i padroncini coinvolti; "Parti uguali" divide tra quelli già compilati (o tutti se vuoti).</div>
      <Input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca padroncino per codice o nome..." className="h-8" data-testid="split-search" />
      <div className="max-h-72 overflow-auto border rounded-sm">
        <table className="autotrans-table w-full">
          <tbody>
            <tr className="font-semibold" data-testid="split-row-verifica">
              <td className="font-mono text-xs w-20">?</td>
              <td>DA RICONTROLLARE <span className="text-xs font-normal text-muted-foreground">(resta in Coda eccezioni, senza padroncino)</span></td>
              <td className="w-36"><Input type="number" step="0.01" className="h-7 text-right font-mono" value={quote[VERIFICA] ?? ""}
                onChange={(e) => setQuote({ ...quote, [VERIFICA]: e.target.value })} data-testid="split-quota-verifica" /></td>
            </tr>
            <tr className="font-semibold" data-testid="split-row-azienda">
              <td className="font-mono text-xs w-20">AZ</td>
              <td>AZIENDA <span className="text-xs font-normal text-muted-foreground">(spese aziendali nel Risultato)</span></td>
              <td className="w-36"><Input type="number" step="0.01" className="h-7 text-right font-mono" value={quote[AZIENDA] ?? ""}
                onChange={(e) => setQuote({ ...quote, [AZIENDA]: e.target.value })} data-testid="split-quota-azienda" /></td>
            </tr>
            {visibili.map((p) => (
              <tr key={p.id}>
                <td className="font-mono text-xs w-20">{p.codice}</td>
                <td>{p.ragione_sociale || `${p.nome} ${p.cognome || ""}`}</td>
                <td className="w-36"><Input type="number" step="0.01" className="h-7 text-right font-mono" value={quote[p.id] ?? ""}
                  onChange={(e) => setQuote({ ...quote, [p.id]: e.target.value })} data-testid={`split-quota-${p.id}`} /></td>
              </tr>
            ))}
            {visibili.length === 0 && <tr><td colSpan={3} className="text-center py-3 text-muted-foreground" data-testid="split-search-empty">Nessun padroncino trovato</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const quoteRows = (quote) => Object.entries(quote).filter(([, v]) => parseFloat(v) > 0)
  .map(([padroncino_id, v]) => ({ padroncino_id, importo: parseFloat(v) }));

export const quoteOk = (quote, totale) =>
  quoteRows(quote).length > 0 && Math.abs(r2(totale - quoteRows(quote).reduce((a, q) => a + q.importo, 0))) < 0.01;

export default function SplitDialog({ movimento, onClose, onDone }) {
  const [padroncini, setPadroncini] = useState([]);
  const [quote, setQuote] = useState({});
  useEffect(() => {
    if (!movimento) return;
    setQuote(movimento.uso_aziendale ? { [AZIENDA]: movimento.importo } : movimento.padroncino_id ? { [movimento.padroncino_id]: movimento.importo } : {});
    api.get("/padroncini", { params: { company: movimento.company } })
      .then((r) => setPadroncini(r.data.filter((p) => p.is_active)));
  }, [movimento]);

  const salva = async () => {
    try {
      const { data } = await api.post(`/movimenti/${movimento.id}/suddividi`, { quote: quoteRows(quote) });
      toast.success(`Spesa suddivisa in ${data.created} quote`);
      onDone();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!movimento} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Suddividi tra più padroncini</DialogTitle></DialogHeader>
        {movimento && <div className="text-sm text-muted-foreground -mt-2">{movimento.descrizione}</div>}
        {movimento && <QuoteEditor padroncini={padroncini} totale={movimento.importo} quote={quote} setQuote={setQuote} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={salva} disabled={!movimento || !quoteOk(quote, movimento.importo)} data-testid="split-save">Suddividi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
